import React, {
  Children,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import {
  Box,
  CardHeader,
  Chip,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  createTheme,
} from "@mui/material";
import {
  AddressDto,
  ContactInfoDTO,
  PatientDto,
  WaitListEntryDto,
} from "../../api-client";
import PatientOverviewHeader from "./PatientOverviewHeader";
import TicketForm from "../Ticketing/TicketEditCreate/ticketForm";
import { MoreVert } from "@mui/icons-material";
import PatientPage from "../PatientDataEntryForm/PatientPage";
import { mobiliTheme } from "../../themes/mobiliTheme";
import {
  FlexListContainer,
  LabelItem,
  ValueItem,
} from "../../molecules/FlexList";
import { observer } from "mobx-react";
import { Archive, ContentCopy, DeleteForever, Map } from "@mui/icons-material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { useNavigate } from "react-router-dom";
import {
  usePatient,
  useArchivePatient,
  usePermanentDeletePatient,
} from "../../stores/Patients";
import CircularProgress from "@mui/material/CircularProgress";
import CreateTicketForm from "../Ticketing/TicketEditCreate/createTicketForm";
import notificationStore from "../../stores/Notification/notificationStore";
import useStore from "../../helpers/useStore";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import { runInAction } from "mobx";
import dayjs from "dayjs";
import { getContrastTextColor } from "../../themes/colorUtils";
import { useLocationId } from "../../stores/User/User.hooks";
import WaitListDataEntryDialog from "./WaitListDataEntryDialog";

interface PatientOverviewProps {
  patientId: number;
  linkOut?: "patientPage" | "calendar";
}

const formatDate = (date: any) => {
  const inputDate = new Date(date);

  const day = String(inputDate.getUTCDate()).padStart(2, "0");
  const year = inputDate.getUTCFullYear();

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = months[inputDate.getUTCMonth()];

  const formattedDate = `${day} ${month} ${year}`;
  return formattedDate;
};

interface ContactToolTipProps {
  contactInfo: ContactInfoDTO;
  children: ReactElement; // Add this line to specify children prop type
}
const ContactToolTip: React.FC<ContactToolTipProps> = ({
  contactInfo,
  children,
}) => {
  return (
    <Tooltip
      title={
        <Box>
          {contactInfo.salutation && (
            <Typography>{contactInfo.salutation}</Typography>
          )}
          {contactInfo.firstNames && (
            <Typography>{contactInfo.firstNames}</Typography>
          )}
          {contactInfo.lastName && (
            <Typography>{contactInfo.lastName}</Typography>
          )}
          {contactInfo.mobile && (
            <Typography>Mobil: {contactInfo.mobile}</Typography>
          )}
          {contactInfo.phone1 && (
            <Typography>Tel. : {contactInfo.phone1}</Typography>
          )}
          {contactInfo.fax && <Typography>Fax: {contactInfo.fax}</Typography>}
          {contactInfo.addressLine1 && (
            <Typography>Adresse: {contactInfo.addressLine1}</Typography>
          )}
          {contactInfo.addressLine2 && (
            <Typography>{contactInfo.addressLine2}</Typography>
          )}
          {contactInfo.city && <Typography>Ort: {contactInfo.city}</Typography>}
          {contactInfo.zip && <Typography>PLZ: {contactInfo.zip}</Typography>}
          {contactInfo.email && (
            <Typography>Mail: {contactInfo.email}</Typography>
          )}
        </Box>
      }
    >
      {children}
    </Tooltip>
  );
};

const CopyButton: React.FC<{ text: string }> = ({ text }) => (
  <Tooltip title="Kopieren">
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text);
      }}
      sx={{ p: 0.25 }}
    >
      <ContentCopy sx={{ fontSize: "0.9rem" }} />
    </IconButton>
  </Tooltip>
);

const GoogleMapsButton: React.FC<{ address: string }> = ({ address }) => (
  <Tooltip title="In Google Maps öffnen">
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        window.open(
          `https://www.google.de/maps/search/?api=1&query=${encodeURIComponent(address)}`,
          "_blank",
          "noopener,noreferrer"
        );
      }}
      sx={{ p: 0.25 }}
    >
      <Map sx={{ fontSize: "0.9rem" }} />
    </IconButton>
  </Tooltip>
);

const buildAddressString = (contactInfo: ContactInfoDTO | undefined): string =>
  [contactInfo?.addressLine1, contactInfo?.zip, contactInfo?.city].filter(Boolean).join(" ");
// Small badge component showing sublocality abbreviation with color
export const SublocalityBadge: React.FC<{
  sublocality?: { abbreviation?: string; color?: string };
}> = ({ sublocality }) => {
  if (!sublocality?.abbreviation) return null;

  const abbreviation = sublocality.abbreviation;
  const fontSize = abbreviation.length > 3 ? "0.5rem" : "0.65rem";
  const bgColor = sublocality.color || "grey";

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: bgColor,
        color: getContrastTextColor(bgColor),
        fontSize: fontSize,
        // fontWeight: "bold",
        marginRight: "2px",
        borderRadius: "3px",
        padding: "1px 2px",
        minWidth: "20px",
        textAlign: "center",
      }}
    >
      {abbreviation}
    </Box>
  );
};

const FlexListAddresses: React.FC<{ addresses: AddressDto[] | undefined }> = ({
  addresses,
}: {
  addresses: AddressDto[] | undefined;
}) => {
  if (!addresses) return <Typography>Keine Adressen vorhanden</Typography>;

  const primary = addresses.find((address) => address.isPrimary);
  if (!primary) return <Typography>Keine Hauptadresse vorhand</Typography>;

  const otherAddresses = addresses.filter(
    (address) =>
      !address.isPrimary && (address.isAlternativeBilling || address.isVisit),
  );

  const primaryAddress = buildAddressString(primary.contactInfo);

  return (
    <>
      {primary.contactInfo?.mobile && (
        <>
          <LabelItem label="Mobil" />
          <Grid item xs={8} sx={{ position: "relative" }}>
            <Box sx={{ position: "absolute", left: -20, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", zIndex: 1 }}>
              <CopyButton text={primary.contactInfo.mobile} />
            </Box>
            <Chip label={primary.contactInfo.mobile} sx={{ width: "100%", justifyContent: "space-between", height: "unset" }} />
          </Grid>
        </>
      )}
      {primary.contactInfo?.phone1 && (
        <>
          <LabelItem label="Tel. 1" />
          <Grid item xs={8} sx={{ position: "relative" }}>
            <Box sx={{ position: "absolute", left: -20, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", zIndex: 1 }}>
              <CopyButton text={primary.contactInfo.phone1} />
            </Box>
            <Chip label={primary.contactInfo.phone1} sx={{ width: "100%", justifyContent: "space-between", height: "unset" }} />
          </Grid>
        </>
      )}
      {/* {primary.contactInfo?.phone2 && (
        <>
          <LabelItem label="Tel1" />
          <ValueItem value={`${primary.contactInfo?.phone2}`} />
        </>
      )} */}
      {primary.contactInfo?.email && (
        <>
          <LabelItem label="Mail" />
          <ValueItem value={`${primary.contactInfo?.email}`} />
        </>
      )}
      <LabelItem label="Kontakt für" />
      <ValueItem
        value={`Hauptkontakt ${primary.isVisit ? "Behandlung" : ""} ${
          primary.isAlternativeBilling ? "Rechnung" : ""
        }`}
      />
      <LabelItem label="Adresse" />
      <Grid item xs={8} sx={{ position: "relative" }}>
        <Box sx={{ position: "absolute", left: -38, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", zIndex: 1 }}>
          <CopyButton text={primaryAddress} />
          <GoogleMapsButton address={primaryAddress} />
        </Box>
        <ContactToolTip contactInfo={primary.contactInfo!}>
          <Chip
            label={
              <>
                <SublocalityBadge sublocality={primary.sublocality} />
                {primary.contactInfo?.addressLine1} {primary.contactInfo?.zip}{" "}{primary.contactInfo?.city}
              </>
            }
            sx={{ width: "100%", justifyContent: "space-between", height: "unset" }}
          />
        </ContactToolTip>
      </Grid>

      {otherAddresses.map((address, index) => {
        const altAddress = buildAddressString(address.contactInfo);
        return (
          <React.Fragment key={address.id ?? `${address.contactInfo?.addressLine1 ?? "address"}-${index}`}>
            <LabelItem label="alt. Kontakt für" />
            <ValueItem
              value={`${address.isVisit ? "Behandlung" : ""} ${
                address.isAlternativeBilling ? "Rechnung" : ""
              }`}
            />

            <LabelItem label="alt. Adresse" />
            <Grid item xs={8} sx={{ position: "relative" }}>
              <Box sx={{ position: "absolute", left: -38, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", zIndex: 1 }}>
                <CopyButton text={altAddress} />
                <GoogleMapsButton address={altAddress} />
              </Box>
              <ContactToolTip contactInfo={address.contactInfo!}>
                <Chip
                  label={
                    <>
                      {address.isVisit && <SublocalityBadge sublocality={address.sublocality} />}
                      {address.contactInfo?.addressLine1} {address.contactInfo?.zip}{" "}{address.contactInfo?.city}
                    </>
                  }
                  sx={{ width: "100%", justifyContent: "space-between", height: "unset" }}
                />
              </ContactToolTip>
            </Grid>
          </React.Fragment>
        );
      })}
    </>
  );
};

const PatientOverview: React.FC<PatientOverviewProps> = observer(
  ({ patientId, linkOut }) => {
    const locationId = useLocationId();
    // Fetch patient data using React Query
    const {
      data: patientData,
      isLoading,
      error,
      refetch,
    } = usePatient(patientId, !!patientId, locationId);

    // Access CalendarStore to update patient names when data is loaded
    const CalendarStore = useStore(CalendarContext);

    // Use React Query mutations instead of direct store calls
    const [waitlistDialogOpen, setWaitlistDialogOpen] = useState(false);
    const archivePatientMutation = useArchivePatient();
    const deletePatientMutation = usePermanentDeletePatient();

    const [menu, setMenu] = useState(null);
    const [openPatientEdit, setOpenPatientEdit] = useState<boolean>(false);
    const [refresh, setRefresh] = useState(false);
    const [openTicket, setOpenTicket] = useState<any>(false);
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [confirmationText, setConfirmationText] = useState("");
    const navigate = useNavigate();

    // Update CalendarStore with patient lastName when data is loaded
    useEffect(() => {
      if (patientData && patientData.lastName) {
        runInAction(() => {
          // Update the lastName in the store based on which patient this is
          if (CalendarStore.selectedPatientId === patientId) {
            CalendarStore.setSelectedPatientLastName(patientData.lastName);
          }
          if (CalendarStore.targetPatientId === patientId) {
            CalendarStore.setTargetPatientLastName(patientData.lastName);
          }
          if (CalendarStore.secondaryPatientId === patientId) {
            CalendarStore.setSecondaryPatientLastName(patientData.lastName);
          }
        });
      }
    }, [patientData, patientId, CalendarStore]);

    const handleMenuOpen = (event: any) => {
      setMenu(event.currentTarget);
    };

    const handleMenuClose = () => {
      setMenu(null);
    };

    const handlePatientClose = () => {
      setOpenPatientEdit(false);
    };
    const handleCloseTicketForm = () => {
      setOpenTicket(false);
    };

    const handleArchive = async () => {
      if (!patientData?.id) return;
      archivePatientMutation.mutate(patientData.id, {
        onSuccess: () => {
          setArchiveDialogOpen(false);
          // Nach erfolgreichem Archivieren: Seite aktualisieren für elegante User Experience
          window.location.reload();
        },
      });
    };

    const handleDelete = async () => {
      if (!patientData?.id) return;
      deletePatientMutation.mutate(
        { patientId: patientData.id, confirmationText },
        {
          onSuccess: () => {
            setDeleteDialogOpen(false);
            // Nach erfolgreichem Löschen: Seite aktualisieren für elegante User Experience
            window.location.reload();
          },
        },
      );
    };

    /**
     * As we have a list of copayment exemptions, we need to determine the correct label to show
     * Logic:
     * - If patient is a minor (< 18), show "befreit (minderjährig)" — they are auto-exempt
     * - If no exemptions, show "pflichtig"
     * - If exemptions exist, extract the years from the endDate (or startDate if endDate is missing - Although this should not happen)
     * - If no valid years can be extracted, show "befreit" (This handles cases where dates are missing or invalid - should not happen)
     * - Otherwise, show "befreit <years>" where <years> is a comma-separated list of unique years
     * Nafees
     */
    const copaymentStatusLabel = useMemo(() => {
      // Minors are automatically exempt from copayment
      if (patientData?.dob) {
        const isMinor = dayjs().diff(dayjs(patientData.dob), "year") < 18;
        if (isMinor) {
          return "befreit (minderjährig)";
        }
      }

      const exemptions = patientData?.copaymentExemptions || [];
      if (!exemptions.length) {
        return "pflichtig";
      }

      const years = new Set<number>();
      exemptions.forEach((exemption) => {
        const representativeDate = exemption.endDate || exemption.startDate;
        if (!representativeDate) {
          return;
        }
        const parsed = dayjs(representativeDate);
        if (parsed.isValid()) {
          years.add(parsed.year());
        }
      });

      if (years.size === 0) {
        return "befreit";
      }

      const sortedYears = Array.from(years)
        .sort((a, b) => a - b)
        .join(", ");
      return `befreit ${sortedYears}`;
    }, [patientData?.copaymentExemptions, patientData?.dob]);

    // Show loading state while fetching patient data
    if (isLoading) {
      return (
        <Card>
          <CardContent
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </CardContent>
        </Card>
      );
    }

    // Show error state if patient data failed to load
    if (error) {
      return (
        <Card>
          <CardContent>
            <Typography color="error">
              Fehler beim Laden der Patientendaten: {error.message}
            </Typography>
          </CardContent>
        </Card>
      );
    }

    // Don't render if no patient data
    if (!patientData) {
      return null;
    }
    if (patientData && !patientData.location && !isLoading) {
      notificationStore.showMessage(
        `Kein Standort für den Patienten gefunden: ${patientData.id}`,
        "warning",
      );
    }

    return (
      <>
        <Card>
          <CardHeader
            // MuiCardHeader-content defaults to `min-width: auto`, which stops
            // it shrinking below its content — the header's name would then
            // overflow instead of ellipsizing. This is what actually lets
            // PatientOverviewHeader hold one line.
            sx={{ "& .MuiCardHeader-content": { minWidth: 0 } }}
            title={
              <PatientOverviewHeader patient={patientData} linkOut={linkOut} />
            }
            action={
              <div>
                <IconButton
                  aria-label="more"
                  aria-controls="menu"
                  aria-haspopup="true"
                  onClick={handleMenuOpen}
                >
                  <MoreVert />
                </IconButton>
                <Menu
                  id="menu"
                  anchorEl={menu}
                  open={Boolean(menu)}
                  onClose={handleMenuClose}
                >
                  <MenuItem
                    onClick={() => {
                      setOpenTicket(!openTicket);
                      handleMenuClose();
                    }}
                  >
                    Ticket erstellen
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setOpenPatientEdit(!openPatientEdit);
                      handleMenuClose();
                    }}
                  >
                    Bearbeiten
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      setWaitlistDialogOpen(true);
                      handleMenuClose();
                    }}
                  >
                    Warteliste verwalten
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      setArchiveDialogOpen(true);
                      handleMenuClose();
                    }}
                  >
                    <Archive fontSize="small" style={{ marginRight: 8 }} />{" "}
                    Archivieren
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setDeleteDialogOpen(true);
                      handleMenuClose();
                    }}
                  >
                    <DeleteForever
                      fontSize="small"
                      style={{ marginRight: 8 }}
                    />{" "}
                    Löschen
                  </MenuItem>
                </Menu>
              </div>
            }
          />
          {openTicket && (
            <CreateTicketForm
              openTicket={openTicket}
              onCloseTicketForm={handleCloseTicketForm}
              data={{ patientId: patientData.id, patient: patientData }}
              refresh={refresh}
              setRefresh={setRefresh}
            />
          )}
          <WaitListDataEntryDialog
            open={waitlistDialogOpen}
            patientId={patientData.id}
            onClose={() => setWaitlistDialogOpen(false)}
            onSuccess={() => refetch()} // refresh patient data
          />

          {openPatientEdit && (
            //   <Dialog
            //   open={openPatientEdit}
            //   onClose={handlePatientClose}
            //   aria-labelledby="edit-patient-dialog"
            //   fullScreen
            // >
            //   <DialogContent>
            <PatientPage
              isEditing={true}
              selectedPatientId={patientId!}
              useDialog={true}
              openPatientDialog={openPatientEdit}
              onClosePatientDialog={handlePatientClose}
            />
            //   </DialogContent>
            //   <DialogActions>
            //     <Button onClick={handlePatientClose}>Close</Button>
            //   </DialogActions>
            // </Dialog>
          )}
          <CardContent sx={{ padding: 1 }}>
            <FlexListContainer>
              <LabelItem label="Name" />
              <ValueItem
                value={`${patientData?.salutation || ""} ${patientData?.firstName} ${
                  patientData?.lastName
                }`}
              />
              <LabelItem label="Geburtsdatum" />
              <ValueItem value={`${formatDate(patientData?.dob) || "-"}`} />
              <LabelItem label="Status" />
              <ValueItem value={`${patientData?.status || "-"}`} />
              <LabelItem label="Kostenträger" />
              <ValueItem
                value={`${(patientData?.payer as any)?.name || "-"}`}
              />
              <LabelItem label="Tarif Group" />
              <ValueItem value={`${patientData?.tarifGroupId || "-"}`} />
              <LabelItem label="Zuzahlung" />
              <ValueItem
                // coPayment flag indicates that the patient is exempted from copayment
                value={copaymentStatusLabel}
              />
              <FlexListAddresses addresses={patientData?.addresses} />
            </FlexListContainer>
          </CardContent>
        </Card>
        {/* Archive Dialog */}
        <Dialog
          open={archiveDialogOpen}
          onClose={() => setArchiveDialogOpen(false)}
        >
          <DialogTitle>Patient archivieren</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Möchten Sie den Patienten wirklich archivieren? Dieser Vorgang
              kann rückgängig gemacht werden.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setArchiveDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleArchive}
              color="primary"
              disabled={archivePatientMutation.isPending}
              startIcon={<Archive />}
            >
              Archivieren
            </Button>
          </DialogActions>
        </Dialog>
        {/* Löschen Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Patient endgültig löschen</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Möchten Sie den Patienten wirklich <b>unwiederbringlich</b>{" "}
              löschen? Bitte geben Sie zur Bestätigung <b>Löschen</b> ein.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Bestätigungstext"
              fullWidth
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleDelete}
              color="error"
              disabled={
                confirmationText !== "Löschen" ||
                deletePatientMutation.isPending
              }
              startIcon={<DeleteForever />}
            >
              Löschen
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  },
);

export default PatientOverview;
